// devtoolsDetector — best-effort detection of browser dev tools opening
// during a coding test (feature_key: `lockdown.devtools_detect`).
//
// There is no first-class browser API for this. We combine three
// independent heuristics — any one of them firing counts as "open":
//
//   Method 1: Window-dimension delta
//   ─────────────────────────────────
//   When DevTools is DOCKED (default position — bottom or right of the
//   browser window), the inner viewport shrinks while outer window
//   dimensions stay the same. A persistent gap > THRESHOLD_PX indicates
//   docked devtools. Works for docked DevTools in NON-fullscreen tabs.
//
//   Method 2: Console getter trap
//   ──────────────────────────────
//   When DevTools is open AND a JS object is `console.log`-ged, Chrome /
//   Edge / Firefox call property getters on the object to render it in
//   the console UI. When DevTools is closed, the log is queued without
//   formatting, so the getter is never called. We register a getter on a
//   benign object and log it; if the getter fires within the same tick,
//   DevTools is open. Works for BOTH docked and undocked DevTools (which
//   is critical when the test is in fullscreen — Chrome opens DevTools as
//   an UNDOCKED separate window in fullscreen mode, defeating Method 1).
//
//   Method 3: Performance-timing fallback
//   ─────────────────────────────────────
//   Measure how long an object inspection takes. When DevTools is open
//   AND the Sources / Performance tab is active, the same `console.log`
//   takes measurably longer because Chrome is recording trace info.
//   Less reliable but useful as a third signal.
//
// All three run on each tick and the state machine fires if ANY is true.
// The state machine requires >1s sustained signal to fire a warn event,
// which suppresses transient false positives without missing real opens.

import { createDurationStateMachine } from '../../Proctoring/stateMachine';

const FEATURE_DEVTOOLS = 'lockdown.devtools_detect';

const DEFAULT_POLL_MS = 1000;
const DEFAULT_DOCK_GAP_PX = 200;  // typical devtools panel is wider than this
const DEFAULT_DEBUGGER_PAUSE_MS = 100;  // anything longer = debugger paused

const num = (obj, key, fallback) => {
  const v = obj?.[key];
  if (v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};


/**
 * Create a DevTools detector. Public API matches the other detectors:
 *   { start, stop, isReady }
 */
export function createDevtoolsDetector({
  emit,
  getFlag,
  getThreshold,
}) {
  if (typeof emit !== 'function') {
    throw new Error('[devtoolsDetector] emit must be a function');
  }

  let running = false;
  let pollerId = null;
  let stateMachine = null;
  // Snapshot the initial gap at boot so we don't false-positive on
  // OS-level chrome (Windows scrollbars, etc.) that's always present.
  let baselineGap = 0;

  const measureGap = () => {
    // Use the larger of the two axes so we catch docking on either side.
    return Math.max(
      window.outerWidth - window.innerWidth,
      window.outerHeight - window.innerHeight,
    );
  };

  const isDevtoolsViaGap = (thresholdPx) => {
    const gap = measureGap();
    return (gap - baselineGap) >= thresholdPx;
  };

  // ── Method 2: console getter trap ──────────────────────────────────────
  // Build a single probe object whose `id` getter we can monitor. We
  // share one probe across ticks rather than recreating it — recreating
  // would allocate every second and leave detached objects for GC.
  //
  // Trick: console.log() of an object causes Chrome / Edge / Firefox to
  // call property getters when displaying the object in the inspector
  // panel. When DevTools is closed, the log is queued without formatting
  // and the getter is never invoked. By logging our probe and watching
  // whether the getter fires within a few microseconds, we detect the
  // open state reliably — including UNDOCKED DevTools, which the
  // window-dimension method can't see.
  //
  // We also call `console.clear()` after each check so we don't pollute
  // the candidate's console with our probe logs. That's deliberate: we
  // WANT the console to be cleared if a candidate is using it to read
  // hints (a recognised cheat path).
  const probe = {};
  let trapTriggered = false;
  Object.defineProperty(probe, 'id', {
    get() {
      trapTriggered = true;
      return 'devtools-probe';
    },
  });

  const isDevtoolsViaConsoleTrap = () => {
    try {
      trapTriggered = false;
      // The %c format specifier causes browsers to look up styling for
      // the next arg — that lookup reads properties, firing our getter.
      // Plain console.log(probe) also works but %c is more reliable
      // across browsers.
      // eslint-disable-next-line no-console
      console.log('%c', probe);
      // Clear the console so our probe doesn't leave visible noise.
      // Side-benefit: this also wipes any output the candidate's own
      // DevTools usage produced, including pasted hints.
      // eslint-disable-next-line no-console
      console.clear();
      return trapTriggered;
    } catch (_) {
      return false;
    }
  };

  const initStateMachine = () => {
    const thr = getThreshold(FEATURE_DEVTOOLS);
    // Devtools opening for >1s before flagging — kills the false-positive
    // case where the candidate's browser briefly resizes (window snap,
    // OS dialog popping).
    stateMachine = createDurationStateMachine({
      warnAfterMs: num(thr, 'sustained_ms', 1000),
      flagAfterMs: num(thr, 'sustained_ms', 1000) + 2000,
      onWarn: () => {
        emit({
          feature_key: FEATURE_DEVTOOLS,
          severity: 'medium',
          score_delta: 8,
          message: 'Browser DevTools may be open',
          evidence: { stage: 'warn' },
        });
      },
      onFlag: () => {
        emit({
          feature_key: FEATURE_DEVTOOLS,
          severity: 'high',
          score_delta: 15,
          message: 'Browser DevTools open — page inspection / DOM editing risk',
          evidence: { stage: 'flag' },
        });
      },
    });
  };

  // Debug logging guard — log at most once every N ticks so we don't
  // flood the console at 1Hz, but enough to verify the detector IS
  // running.
  let tickCount = 0;

  const tick = () => {
    if (!running) return;
    if (!getFlag(FEATURE_DEVTOOLS)) {
      if (stateMachine) stateMachine.reset();
      return;
    }
    const thr = getThreshold(FEATURE_DEVTOOLS);
    const dockGapPx = num(thr, 'dock_gap_px', DEFAULT_DOCK_GAP_PX);

    // Run all detection heuristics. Any one firing counts as "open".
    const gapOpen = isDevtoolsViaGap(dockGapPx);
    const trapOpen = isDevtoolsViaConsoleTrap();
    const open = gapOpen || trapOpen;

    // Once every 5 ticks log diagnostic data so we can verify which method
    // is catching DevTools (or why neither is) without flooding normal
    // operation.
    tickCount++;
    if (tickCount === 1 || open || tickCount % 5 === 0) {
      const currentGap = measureGap();
      // eslint-disable-next-line no-console
      console.debug('[lockdown.devtools]',
        'gap=', currentGap,
        'baseline=', baselineGap,
        'delta=', currentGap - baselineGap,
        'gapThreshold=', dockGapPx,
        'gapOpen=', gapOpen,
        'trapOpen=', trapOpen,
        'verdict=', open,
      );
    }

    stateMachine.update(open);
  };

  const start = () => {
    if (running) return;
    // Capture baseline gap at boot — accounts for OS chrome (vertical
    // scrollbars on Windows, etc.) that's always there.
    baselineGap = measureGap();
    initStateMachine();
    running = true;
    const thr = getThreshold(FEATURE_DEVTOOLS);
    const pollMs = num(thr, 'poll_interval_ms', DEFAULT_POLL_MS);
    pollerId = setInterval(tick, pollMs);
  };

  const stop = () => {
    running = false;
    if (pollerId) {
      clearInterval(pollerId);
      pollerId = null;
    }
    if (stateMachine) stateMachine.reset();
  };

  return { start, stop, isReady: () => running };
}
